import { X, Calendar, Gauge, Zap, MapPin, BadgeCheck, Wrench, Award, Shield, Bike, Video, KeyRound } from "lucide-react";
import { Button } from "../ui/button";
import { formatEzDate } from "../../lib/api";

/**
 * Phase 3.62 — Vorschau-Modal für CreateListingPage.
 *
 * Rendert den aktuellen Form-State so, wie Käufer das Inserat sehen werden
 * — als verkleinerte Variante von ListingDetailPage. Reduziert nachträgliche
 * Edits, weil Inserenten ihre Eingaben in Originallayout prüfen können.
 *
 * Props:
 *   form        — Form-State aus CreateListingPage
 *   open        — boolean
 *   onClose     — () => void
 *   onConfirm   — () => void (löst echtes submit() aus)
 *   submitting  — boolean (Button-Loading-State)
 *   isEdit      — boolean (für Button-Label)
 *   user        — aktueller Händler (für Anbieter-Info-Box)
 */
export default function ListingPreviewModal({ form, open, onClose, onConfirm, submitting, isEdit, user }) {
    if (!open) return null;

    const isMoto = form.category === "motorcycle";
    const colorFinishLabel = form.color_finish === "matt" ? "matt" : form.color_finish === "glaenzend" ? "glänzend" : null;
    const colorDisplay = [form.color, colorFinishLabel].filter(Boolean).join(" · ") || null;

    const UMWELTPLAKETTE_LABEL = { rot: "Rot", gruen: "Grün", blau: "Blau", keine: "Keine", nicht_bekannt: "Nicht bekannt" };
    const WARRANTY_TYPE_LABEL = { hersteller: "Hersteller-Garantie", drittanbieter: "Drittanbieter-Garantie", gesetzlich: "Gesetzliche Gewährleistung" };
    const formatMonthYear = (v) => {
        if (!v) return null;
        const m = /^(\d{4})-(\d{2})/.exec(v);
        return m ? `${m[2]}/${m[1]}` : v;
    };
    const formatPrice = (p) => p != null && p !== "" ? `${Number(p).toLocaleString("de-DE")} €` : "—";
    const formatMileage = (m) => m != null && m !== "" ? `${Number(m).toLocaleString("de-DE")} km` : "—";

    const specs = isMoto
        ? [
              { label: "Erstzulassung", value: formatEzDate(form.first_registration_date || form.year) },
              { label: "Kilometerstand", value: form.mileage ? formatMileage(form.mileage) : null },
              { label: "Leistung", value: form.power_hp ? `${form.power_hp} PS (${form.power_kw} kW)` : (form.power_kw ? `${form.power_kw} kW` : null) },
              { label: "Hubraum", value: form.displacement ? `${form.displacement} ccm` : null },
              { label: "Zylinder", value: form.cylinder_count ? String(form.cylinder_count) : null },
              { label: "Kraftstoff", value: form.fuel },
              { label: "Antrieb", value: form.drive_type },
              { label: "Getriebe", value: form.transmission },
              { label: "Schadstoffklasse", value: form.emission_class },
              { label: "Umweltplakette", value: UMWELTPLAKETTE_LABEL[form.umweltplakette] || null },
              { label: "Farbe Hersteller", value: colorDisplay },
              { label: "Farbbasis", value: form.color_base },
              { label: "Zustand", value: form.condition },
              { label: "Vorbesitzer", value: form.previous_owners !== "" ? String(form.previous_owners) : null },
              { label: "Garantie", value: form.warranty_type ? `${WARRANTY_TYPE_LABEL[form.warranty_type]}${form.warranty_months ? ` · ${form.warranty_months} Mon.` : ""}` : (form.warranty_months ? `${form.warranty_months} Monate` : null) },
              { label: "Letzte Wartung", value: formatMonthYear(form.last_service_date) },
              { label: "TÜV bis", value: formatMonthYear(form.tuev_until) },
              { label: "HU/AU bis", value: form.next_inspection_date },
              { label: "Reichweite", value: form.range_km ? `${form.range_km} km` : null },
              { label: "Ladedauer", value: form.charging_time_hours ? `${form.charging_time_hours} h` : null },
          ]
        : [
              { label: "Baujahr", value: form.year },
              { label: "Kilometerstand", value: form.mileage ? formatMileage(form.mileage) : null },
              { label: "Typ", value: form.bike_type },
              { label: "Rahmenmaterial", value: form.frame_material },
              { label: "Rahmengröße", value: form.frame_size },
              { label: "Laufradgröße", value: form.wheel_size },
              { label: "Schaltung", value: form.gears ? `${form.gears}-Gang` : null },
              { label: "Antrieb", value: form.drive_type },
              { label: "Farbe Hersteller", value: colorDisplay },
              { label: "Farbbasis", value: form.color_base },
              { label: "Zustand", value: form.condition },
          ];

    const trustBadges = [
        form.accident_free === true && { icon: BadgeCheck, label: "Unfallfrei" },
        form.has_service_book === true && { icon: Wrench, label: "Scheckheftgepflegt" },
        form.workshop_inspected === true && { icon: Wrench, label: "Werkstatt-geprüft" },
        form.warranty_months > 0 && { icon: Award, label: `Garantie ${form.warranty_months} Mon.` },
        form.emission_class && { icon: Shield, label: form.emission_class },
        form.battery_certificate === true && { icon: Shield, label: "Batteriezertifikat" },
    ].filter(Boolean);

    const filledSpecs = specs.filter((s) => s.value);
    const missingCount = specs.length - filledSpecs.length;

    return (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-start justify-center p-2 md:p-6 overflow-y-auto" data-testid="listing-preview-modal">
            <div className="bg-white rounded-sm w-full max-w-5xl my-4 shadow-2xl">
                {/* Sticky preview header */}
                <div className="sticky top-0 z-10 bg-[#111827] text-white px-5 py-3 flex items-center justify-between rounded-t-sm">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#FFCC00]">Vorschau</div>
                        <div className="font-display font-bold text-sm">So sehen Käufer dein Inserat</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={onClose} className="text-white/70 hover:text-white p-1" data-testid="preview-close-btn" aria-label="Schließen">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-4 md:p-6">
                    {/* Left: gallery + content */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Image */}
                        <div className="relative bg-gray-100 rounded-sm overflow-hidden aspect-[4/3]">
                            {form.images?.[0] ? (
                                <img src={form.images[0]} alt={form.title || "Vorschau"} className="w-full h-full object-cover" data-testid="preview-main-image" />
                            ) : (
                                <div className="flex items-center justify-center w-full h-full text-gray-400 text-sm">Noch keine Bilder hochgeladen</div>
                            )}
                            {form.image_labels?.[0] && (
                                <div className="absolute top-3 left-3 bg-black/75 text-white text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-sm">
                                    {form.image_labels[0]}
                                </div>
                            )}
                            {form.images?.length > 1 && (
                                <div className="absolute bottom-3 right-3 bg-black/70 text-white text-[11px] px-2 py-1 rounded-sm font-mono-num">+{form.images.length - 1} weitere</div>
                            )}
                        </div>

                        {/* Title block */}
                        <div className="bg-white border border-gray-200 rounded-sm p-5">
                            <h1 className="font-display text-2xl font-black tracking-tight mb-3" data-testid="preview-title">{form.title || <span className="text-gray-400">— Titel fehlt —</span>}</h1>
                            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-700 mb-3">
                                <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-gray-400" /> EZ {formatEzDate(form.first_registration_date || form.year)}</span>
                                <span className="flex items-center gap-1.5"><Gauge className="w-4 h-4 text-gray-400" /> {form.mileage ? formatMileage(form.mileage) : "—"}</span>
                                {form.power_hp && <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-gray-400" /> {form.power_hp} PS</span>}
                                <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gray-400" /> {[form.street, form.location].filter(Boolean).join(", ") || "—"}</span>
                            </div>
                            {trustBadges.length > 0 && (
                                <div className="flex flex-wrap gap-2" data-testid="preview-trust-badges">
                                    {trustBadges.map((b, i) => {
                                        const Icon = b.icon;
                                        return (
                                            <span key={i} className="inline-flex items-center gap-1.5 bg-green-50 text-green-800 border border-green-200 rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-wider">
                                                <Icon className="w-3.5 h-3.5" /> {b.label}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Specs */}
                        <div className="bg-white border border-gray-200 rounded-sm p-5">
                            <h2 className="font-display text-lg font-black mb-3 flex items-baseline gap-3">
                                Technische Daten
                                {missingCount > 0 && (
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600">{missingCount} Feld{missingCount === 1 ? "" : "er"} fehlt</span>
                                )}
                            </h2>
                            {filledSpecs.length === 0 ? (
                                <p className="text-sm text-gray-500">Noch keine technischen Daten ausgefüllt.</p>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6" data-testid="preview-specs-grid">
                                    {filledSpecs.map((s) => (
                                        <div key={s.label} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                                            <span className="text-gray-500 font-medium">{s.label}</span>
                                            <span className="font-semibold text-[#111827] text-right">{s.value}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Features / Accessories */}
                        {((form.features?.length || 0) > 0 || (form.accessories?.length || 0) > 0) && (
                            <div className="bg-white border border-gray-200 rounded-sm p-5">
                                {form.features?.length > 0 && (
                                    <div className="mb-3">
                                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Ausstattung</h3>
                                        <div className="flex flex-wrap gap-1.5">
                                            {form.features.map((f) => (
                                                <span key={f} className="inline-flex items-center bg-gray-100 text-[#111827] border border-gray-200 rounded-sm px-2.5 py-1 text-xs font-semibold">{f}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {form.accessories?.length > 0 && (
                                    <div>
                                        <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Zubehör</h3>
                                        <div className="flex flex-wrap gap-1.5">
                                            {form.accessories.map((a) => (
                                                <span key={a} className="inline-flex items-center bg-[#FFCC00]/15 text-[#111827] border border-[#FFCC00]/40 rounded-sm px-2.5 py-1 text-xs font-semibold">{a}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Description */}
                        <div className="bg-white border border-gray-200 rounded-sm p-5">
                            <h2 className="font-display text-lg font-black mb-2">Beschreibung</h2>
                            {form.description ? (
                                <p className="text-sm text-gray-700 whitespace-pre-line leading-relaxed" data-testid="preview-description">{form.description}</p>
                            ) : (
                                <p className="text-sm text-amber-600">— Beschreibung fehlt — Käufer wünschen sich eine kurze Vorstellung des Fahrzeugs.</p>
                            )}
                        </div>

                        {/* Video */}
                        {form.video_url && (
                            <div className="bg-white border border-gray-200 rounded-sm p-5">
                                <h2 className="font-display text-lg font-black mb-2 flex items-center gap-2">
                                    <Video className="w-4 h-4 text-[#FFCC00]" /> Video
                                </h2>
                                <p className="text-xs text-gray-500 truncate">{form.video_url}</p>
                            </div>
                        )}

                        {/* Sharing */}
                        {form.sharing_enabled && (
                            <div className="bg-white border-2 border-[#FFCC00] rounded-sm p-5">
                                <div className="flex items-center gap-2 mb-2">
                                    <Bike className="w-4 h-4 text-[#FFCC00]" />
                                    <h2 className="font-display text-lg font-black">Auch mietbar / Sharing</h2>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                                    {form.sharing_price_per_day && (
                                        <div className="flex justify-between py-1.5 text-sm border-b border-gray-100">
                                            <span className="text-gray-500">Preis pro Tag</span>
                                            <span className="font-bold font-mono-num">{formatPrice(form.sharing_price_per_day)}</span>
                                        </div>
                                    )}
                                    {form.sharing_pickup_address && (
                                        <div className="flex justify-between py-1.5 text-sm border-b border-gray-100">
                                            <span className="text-gray-500 flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Abholung</span>
                                            <span className="font-semibold text-right">{form.sharing_pickup_address}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right: price card + seller */}
                    <div className="space-y-4">
                        <div className="bg-[#FFCC00] rounded-sm p-5 sticky top-20">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-[#111827]/70 mb-1">Preis</div>
                            <div className="font-display text-3xl font-black text-[#111827] font-mono-num" data-testid="preview-price">{formatPrice(form.price)}</div>
                            {form.sale_type === "auction" && (
                                <div className="mt-1 text-[11px] font-bold uppercase tracking-wider text-[#111827]/80">Auktion · Startpreis</div>
                            )}
                            {form.min_offer_amount && (
                                <div className="mt-2 text-xs text-[#111827]/80">Mindestangebot: {formatPrice(form.min_offer_amount)}</div>
                            )}
                        </div>

                        <div className="bg-white border border-gray-200 rounded-sm p-4">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Anbieter</div>
                            <div className="font-bold text-[#111827]">{user?.company_name || user?.name || "—"}</div>
                            <div className="text-xs text-gray-500">{user?.city || form.location || "—"}</div>
                            {user?.role === "b2b" && (
                                <div className="mt-2 inline-flex items-center gap-1.5 bg-green-50 text-green-800 border border-green-200 rounded-sm px-2 py-1 text-[10px] font-bold uppercase tracking-wider">
                                    <BadgeCheck className="w-3 h-3" /> Gewerblicher Händler
                                </div>
                            )}
                        </div>

                        {missingCount > 5 && (
                            <div className="bg-amber-50 border-l-4 border-amber-400 rounded-sm p-3" data-testid="preview-completeness-warning">
                                <div className="text-xs font-bold text-amber-900 mb-1">Inserat noch unvollständig</div>
                                <div className="text-[11px] text-amber-800">{missingCount} technische Daten fehlen. Inserate mit vollständigen Angaben erzielen messbar mehr Anfragen.</div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Sticky footer with actions */}
                <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 py-3 flex flex-wrap items-center justify-end gap-2 rounded-b-sm">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-sm font-bold uppercase tracking-wider text-xs" data-testid="preview-edit-btn">
                        Weiter bearbeiten
                    </Button>
                    <Button type="button" onClick={onConfirm} disabled={submitting} className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm px-6 uppercase tracking-wider text-xs" data-testid="preview-confirm-btn">
                        {submitting ? "Wird gespeichert…" : isEdit ? "Änderungen speichern" : "Jetzt veröffentlichen"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
